import React, { useState, useEffect, useRef } from 'react';
import { Typography, Card, Form, Input, Button, Radio, Space, Breadcrumb, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { InfoCircleOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { usersApi, User, UpdateUserRequest } from '../services/users';
import { message } from '../utils/message';
import { useNavigationBlocker } from '../hooks/useNavigationBlocker';

const { Title, Text } = Typography;

interface PermissionFormData {
  adminName: string;
  id: string;
  password?: string;
  email: string;
  phone: string;
  role: 'admin' | 'super_admin' | 'development_admin';
}

const EditPermission: React.FC = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [isModified, setIsModified] = useState(false);
  const [initialData, setInitialData] = useState<PermissionFormData | null>(null);

  // 네비게이션 가드 적용
  useNavigationBlocker(isModified, '/permission-management');
  const [userData, setUserData] = useState<User | null>(null);
  const [passwordValue, setPasswordValue] = useState<string>('');
  const [showPasswordChange, setShowPasswordChange] = useState<boolean>(false);
  const passwordInputRef = useRef<any>(null);
  const id = paramId;

  // 기존 데이터 로드
  useEffect(() => {
    const loadPermissionData = async () => {
      if (!id) {
        navigate('/permission-management');
        return;
      }

      setDataLoading(true);
      try {
        const response = await usersApi.getUser(Number(id));

        if (response.success) {
          const user = response.data;
          // console.log('사용자 데이터:', user); // 디버깅용
          setUserData(user);

          const formData: PermissionFormData = {
            adminName: user.name,
            id: user.username,
            email: user.email,
            phone: user.phone,
            role: user.role,
          };

          // console.log('폼 데이터:', formData); // 디버깅용
          setInitialData(formData);
          form.setFieldsValue(formData);
        } else {
          message.error('사용자 정보를 불러오는데 실패했습니다.');
          navigate('/permission-management');
        }
      } catch (error: any) {
        console.error('사용자 정보 로드 실패:', error);
        message.error('사용자 정보를 불러오는데 실패했습니다.');
        navigate('/permission-management');
      } finally {
        setDataLoading(false);
      }
    };

    loadPermissionData();
  }, [id, form, navigate]);

  const handleSubmit = async (values: PermissionFormData) => {
    if (!userData) return;

    setLoading(true);
    try {
      const updateData: UpdateUserRequest = {
        name: values.adminName,
        email: values.email,
        phone: values.phone,
        role: values.role,
      };

      // 비밀번호 변경이 활성화되고 입력된 경우에만 비밀번호 재설정
      if (showPasswordChange && values.password && values.password.trim()) {
        // 비밀번호 재설정 API 별도 호출
        await usersApi.resetPassword(userData.id, values.password);
      }

      const response = await usersApi.updateUser(userData.id, updateData);

      if (response.success) {
        message.success('성공적으로 저장되었습니다.');
        setIsModified(false); // 네비게이션 가드 해제 (먼저 실행)
        // 상태 변경 후 다음 렌더링 사이클에서 navigate 실행
        setTimeout(() => {
          navigate('/permission-management');
        }, 0);
      } else {
        message.error('사용자 정보 수정에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('사용자 정보 수정 실패:', error);
      message.error('사용자 정보 수정 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // useNavigationBlocker가 팝업을 처리하므로 직접 navigate만 호출
    navigate('/permission-management');
  };

  if (dataLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        width: '100%'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!initialData) {
    return (
      <div style={{ padding: '0', textAlign: 'center', marginTop: '100px' }}>
        <Text style={{ color: '#ffffff' }}>데이터를 찾을 수 없습니다.</Text>
      </div>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          {
            title: '권한 관리',
            href: '/permission-management',
          },
          {
            title: '권한 수정',
          },
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 제목 */}
      <Title level={2} style={{ margin: '0 0 32px 0' }}>
        권한 수정
      </Title>

      {/* 폼 카드 */}
      <Card
        style={{
          background: '#1f1f1f',
          border: '1px solid #303030'
        }}
        styles={{ body: { padding: '40px' } }}
      >
        <div style={{ marginBottom: '32px' }}>
          <Title level={4} style={{ margin: '0 0 24px 0', color: '#ffffff' }}>
            기본 정보
          </Title>
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
          size="large"
        
        onValuesChange={() => setIsModified(true)}
      >
          {/* 관리자명 */}
          <Form.Item
            label={<Text style={{ color: '#ffffff' }}>관리자명 <span style={{ color: '#ff4d4f' }}>*</span></Text>}
            name="adminName"
            rules={[
              { required: true, message: '관리자명을 입력하세요.' }
            ]}
            style={{ maxWidth: '600px' }}
          >
            <Input
              placeholder="관리자명을 입력하세요."
              style={{
                height: '48px',
                background: '#141414',
                border: '1px solid #303030',
                color: '#ffffff'
              }}
            />
          </Form.Item>

          {/* ID (수정 불가) */}
          <Form.Item
            label={<Text style={{ color: '#ffffff' }}>ID <span style={{ color: '#ff4d4f' }}>*</span></Text>}
            name="id"
            style={{ maxWidth: '600px' }}
          >
            <Input
              disabled
              style={{
                height: '48px',
                background: '#0a0a0a',
                border: '1px solid #303030',
                color: '#666666'
              }}
            />
          </Form.Item>

          {/* 비밀번호 */}
          <Form.Item
            label={<Text style={{ color: '#ffffff' }}>비밀번호 <span style={{ color: '#ff4d4f' }}>*</span></Text>}
            name="password"
            rules={showPasswordChange ? [
              {
                validator: (_, value) => {
                  if (!value && !showPasswordChange) return Promise.resolve();

                  // 1순위: 길이 검증 (최소/최대)
                  if (value && value.length < 6) {
                    return Promise.reject('최소6자, 최대 20자이여야 합니다.');
                  }
                  if (value && value.length > 20) {
                    return Promise.reject('최소6자, 최대 20자이여야 합니다.');
                  }

                  // 2순위: 패턴 검증 (영문, 숫자, 특수문자)
                  const pattern = /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!#$%&()*+,\-./:;<=>?@[\]^_{|}~₩])[A-Za-z\d!#$%&()*+,\-./:;<=>?@[\]^_{|}~₩]+$/;
                  if (value && !pattern.test(value)) {
                    return Promise.reject('영문, 숫자, 특수문자를 포함해야 합니다.');
                  }

                  return Promise.resolve();
                }
              }
            ] : []}
            style={{ maxWidth: '100%', marginBottom: 0 }}
          >
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Input.Password
                ref={passwordInputRef}
                disabled={!showPasswordChange}
                placeholder={showPasswordChange ? '비밀번호를 입력하세요.' : '********'}
                value={showPasswordChange ? passwordValue : ''}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setPasswordValue(newValue);
                  form.setFieldsValue({ password: newValue });
                }}
                visibilityToggle={showPasswordChange && passwordValue.length > 0}
                iconRender={(visible) =>
                  showPasswordChange ? (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />) : null
                }
                style={{
                  height: '48px',
                  maxWidth: '600px',
                  background: showPasswordChange ? '#141414' : '#0a0a0a',
                  border: '1px solid #303030',
                  color: showPasswordChange ? '#ffffff' : '#666666',
                  flex: 1
                }}
              />
              <Button
                type="primary"
                onClick={() => {
                  const newState = !showPasswordChange;
                  setShowPasswordChange(newState);
                  if (!newState) {
                    // 변경 취소 시 비밀번호 필드 초기화
                    setPasswordValue('');
                    form.setFieldsValue({ password: undefined });
                  } else {
                    // 비밀번호 변경 활성화 시 포커스
                    setTimeout(() => {
                      passwordInputRef.current?.focus();
                    }, 0);
                  }
                }}
                style={{
                  height: '48px',
                  minWidth: '120px',
                  background: '#1890ff',
                  border: 'none'
                }}
              >
                {showPasswordChange ? '취소' : '비밀번호 변경'}
              </Button>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                height: '48px'
              }}>
                <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <Text style={{ color: '#ffffff', fontSize: '12px', lineHeight: '1.2' }}>
                    길이 6~20자, 영문, 숫자, 특수문자가 각 1개 이상 포함되어야 합니다.
                  </Text>
                  <Text style={{ color: '#a6a6a6', fontSize: '11px', lineHeight: '1.2' }}>
                    {'사용 가능 특수문자: ! # $ % & ( ) * + , - . : ; < = > ? @ [ ₩ ] ^ _ { | } ~'}
                  </Text>
                </div>
              </div>
            </div>
          </Form.Item>

          {/* 이메일 */}
          <Form.Item
            label={<Text style={{ color: '#ffffff' }}>이메일 <span style={{ color: '#ff4d4f' }}>*</span></Text>}
            name="email"
            rules={[
              { required: true, message: '이메일을 입력하세요.' },
              { type: 'email', message: '이메일 형식이 올바르지 않습니다.' }
            ]}
            style={{ maxWidth: '600px' }}
          >
            <Input
              placeholder="이메일을 입력하세요."
              style={{
                height: '48px',
                background: '#141414',
                border: '1px solid #303030',
                color: '#ffffff'
              }}
            />
          </Form.Item>

          {/* 휴대전화 */}
          <div style={{ maxWidth: '100%' }}>
            <Form.Item
              label={<Text style={{ color: '#ffffff' }}>휴대전화 <span style={{ color: '#ff4d4f' }}>*</span></Text>}
              style={{ marginBottom: 0 }}
            >
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <Form.Item
                  name="phone"
                  rules={[
                    { required: true, message: '휴대전화를 입력하세요.' },
                    {
                      validator: (_, value) => {
                        if (!value) return Promise.resolve();

                        // 1순위: 숫자만 입력 가능
                        if (!/^\d+$/.test(value)) {
                          return Promise.reject('숫자만 입력 가능합니다.');
                        }

                        // 2순위: 11자 형식 검증
                        if (value.length !== 11) {
                          return Promise.reject('휴대전화 형식이 올바르지 않습니다.');
                        }

                        return Promise.resolve();
                      }
                    }
                  ]}
                  style={{ marginBottom: 0, maxWidth: '600px', flex: 1 }}
                >
                  <Input
                    placeholder="휴대전화를 입력하세요."
                    maxLength={11}
                    style={{
                      height: '48px',
                      background: '#141414',
                      border: '1px solid #303030',
                      color: '#ffffff'
                    }}
                  />
                </Form.Item>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '48px' }}>
                  <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                  <Text style={{ color: '#a6a6a6', fontSize: '14px', whiteSpace: 'nowrap' }}>
                    '-'를 제외하고 입력하세요.
                  </Text>
                </div>
              </div>
            </Form.Item>
          </div>

          {/* 권한 */}
          <Form.Item
            label={<Text style={{ color: '#ffffff' }}>권한 <span style={{ color: '#ff4d4f' }}>*</span></Text>}
            name="role"
            rules={[
              { required: true, message: '권한을 선택하세요.' }
            ]}
            style={{ maxWidth: '600px' }}
          >
            <Radio.Group>
              <Radio value="admin" style={{ color: '#ffffff' }}>Admin</Radio>
              <Radio value="super_admin" style={{ color: '#ffffff' }}>Super Admin</Radio>
            </Radio.Group>
          </Form.Item>

        </Form>
      </Card>

      {/* 하단 버튼 영역 */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '32px',
        width: '100%'
      }}>
        <Button
          size="large"
          onClick={handleCancel}
          style={{
            minWidth: '120px',
            height: '48px',
            background: 'transparent',
            border: '1px solid #303030',
            color: '#ffffff'
          }}
        >
          취소
        </Button>
        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={loading}
          size="large"
          style={{
            minWidth: '120px',
            height: '48px',
            background: '#1890ff',
            border: 'none'
          }}
        >
          수정
        </Button>
      </div>
    </Space>
  );
};

export default EditPermission;